/**
 * Light or dark, and the one place that decides which.
 *
 * The attribute on <html> is the single source of truth. CSS reads it through
 * `:root[data-theme="light"]`, and the chart palette reads it through
 * `currentTheme()` - so there is no second copy of the choice to fall out of
 * step with the first.
 *
 * It lives in its own storage key rather than inside the persisted UI store,
 * because it has to be applied BEFORE React loads: index.html runs a two-line
 * script that reads this key and sets the attribute, or every page load in
 * light mode would paint dark first and then flash. That script can read one
 * plain string; it should not have to parse the whole UI store to find it.
 */

import { create } from "zustand";

export type Theme = "dark" | "light";

/** Also read by the inline script in index.html - keep the two in step. */
export const THEME_KEY = "kinetix.theme";

export function currentTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}

function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Storage unavailable (private mode, blocked): the choice lasts this visit.
  }
}

/*
 * Apply the stored choice here too, not only in index.html.
 *
 * The inline script there exists to stop a flash of the wrong theme on load,
 * and it is the kind of thing a Content-Security-Policy added later by a
 * reverse proxy blocks without a word. If it did not run, the attribute is
 * unset and a user who chose light would get dark every time. So correctness
 * does not depend on it: this runs as the app loads and restores the choice
 * either way, and the script is only ever an optimisation.
 */
if (typeof document !== "undefined" && !document.documentElement.dataset.theme) {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(THEME_KEY);
  } catch {
    // Unavailable: the default applies.
  }
  document.documentElement.dataset.theme = stored === "light" ? "light" : "dark";
}

export const useTheme = create<{ theme: Theme; setTheme: (t: Theme) => void; toggle: () => void }>(
  (set, get) => ({
    theme: currentTheme(),
    setTheme: (theme) => {
      apply(theme);
      set({ theme });
    },
    toggle: () => get().setTheme(get().theme === "dark" ? "light" : "dark"),
  }),
);
